/**
 * VIP page — real page. R2.
 * Data: GET /api/vip-status (auth required).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, ArrowRight, Award, CheckCircle2, Sparkles } from "lucide-react";

import api from "../lib/api";
import { extractError } from "../lib/formatError";
import AppShell from "../components/ui-r1/AppShell";
import Button from "../components/ui-r1/Button";
import { Card, CardBody } from "../components/ui-r1/Card";
import { Badge } from "../components/ui-r1/Badge";
import ChatWidget from "../components/ChatWidget";

const TIER_ACCENT = {
  bronze:   { grad: "from-[#C08A5B]/40 to-[#7A4E30]/10 border-[#C08A5B]/40", chip: "text-[#C08A5B]" },
  silver:   { grad: "from-[#C9D0E0]/40 to-[#7C8299]/10 border-[#C9D0E0]/40", chip: "text-[#C9D0E0]" },
  gold:     { grad: "from-gold/40 to-[#FF9A2E]/10 border-gold/40",           chip: "text-gold" },
  platinum: { grad: "from-purple/40 to-pink/10 border-purple/40",             chip: "text-purple" },
  diamond:  { grad: "from-pink/40 to-purple/10 border-pink/40",               chip: "text-pink" },
};

export default function Vip() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/vip-status").then(({ data }) => setData(data)).catch((e) => setError(extractError(e)));
  }, []);

  return (
    <AppShell activeSection="vip" pageTitle="VIP">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-8 py-6 space-y-8" data-testid="vip-page">
        {/* Hero */}
        <div className="relative overflow-hidden rounded-2xl border border-line bg-gradient-to-br from-purple/10 via-elevated to-pink/10 p-6 sm:p-10">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-gold-glow pointer-events-none" />
          <div className="relative">
            <div className="flex items-center gap-2 mb-3">
              <Badge variant="gold"><Crown className="w-3 h-3 mr-1" /> VIP Program</Badge>
            </div>
            <h1 className="font-display italic text-3xl sm:text-5xl text-ink leading-tight mb-3">
              Play more. <span className="gradient-text">Earn more.</span>
            </h1>
            <p className="text-sm sm:text-base text-ink-2 max-w-lg">
              Automatic tier progression. The more you wager, the higher you climb — cashback, faster withdrawals,
              personal concierge, and more.
            </p>
          </div>
        </div>

        {error && (
          <div className="text-xs text-danger border border-danger/60 bg-danger/10 rounded-lg px-3 py-2">{error}</div>
        )}

        {/* Current status */}
        {data && (
          <Card variant="elevated" data-testid="vip-status-card">
            <CardBody>
              <div className="flex flex-col sm:flex-row sm:items-center gap-6">
                <div className="flex items-center gap-4">
                  <span className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${TIER_ACCENT[data.current_tier]?.grad || TIER_ACCENT.bronze.grad} border flex items-center justify-center`}>
                    <Crown className={`w-7 h-7 ${TIER_ACCENT[data.current_tier]?.chip || "text-ink"}`} />
                  </span>
                  <div>
                    <div className="text-[10px] tracking-[0.35em] uppercase text-ink-3">Your tier</div>
                    <div className="font-display italic text-3xl text-ink" data-testid="vip-current-tier">{data.current_tier_label}</div>
                    <div className="text-[10px] tracking-widest uppercase text-ink-2 mt-1">
                      Wagered · <span className="font-mono text-ink">${data.total_wagered_usd.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                </div>

                {data.next_tier ? (
                  <div className="flex-1 min-w-0">
                    <div className="flex items-end justify-between mb-2">
                      <div className="text-[10px] tracking-widest uppercase text-ink-3">
                        Progress to <span className="text-ink">{data.next_tier_label}</span>
                      </div>
                      <div className="text-[11px] font-mono text-ink-2" data-testid="vip-progress-amount">
                        ${data.amount_to_next_tier_usd.toLocaleString(undefined, { maximumFractionDigits: 2 })} to go
                      </div>
                    </div>
                    <div className="h-3 rounded-full bg-highest overflow-hidden">
                      <div
                        className="h-full bg-brand-gradient transition-all"
                        style={{ width: `${data.progress_pct}%` }}
                        data-testid="vip-progress-bar"
                      />
                    </div>
                    <div className="text-[10px] tracking-widest uppercase text-ink-3 mt-1">
                      {data.progress_pct.toFixed(1)}% of the way there
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-gold">
                    <Sparkles className="w-4 h-4" />
                    <span className="text-sm font-display italic">Top of the ladder</span>
                  </div>
                )}
              </div>

              {/* Current benefits at a glance */}
              <div className="mt-6 pt-6 border-t border-line grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                <BenefitStat label="Cashback" value={`${data.benefits.cashback_pct}%`} />
                <BenefitStat label="Withdrawal" value={data.benefits.withdrawal_speed_hours >= 24 ? `${data.benefits.withdrawal_speed_hours}h` : "Near instant"} />
                <BenefitStat label="Priority chat" value={data.benefits.priority_chat ? "Yes" : "—"} />
                <BenefitStat label="Personal host" value={data.benefits.personal_manager ? "Yes" : "—"} />
              </div>
            </CardBody>
          </Card>
        )}

        {/* Tier ladder */}
        <div>
          <div className="text-[10px] tracking-[0.4em] uppercase text-pink mb-2">Ladder</div>
          <h2 className="font-display italic text-2xl text-ink mb-4">All tiers</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {(data?.tiers || []).map((t) => (
              <TierCard
                key={t.key}
                tier={t}
                active={t.key === data?.current_tier}
                reached={data?.total_wagered_usd >= t.threshold_usd}
              />
            ))}
          </div>
        </div>

        {/* How to reach VIP */}
        <Card>
          <CardBody>
            <div className="text-[10px] tracking-[0.3em] uppercase text-pink mb-2">How it works</div>
            <h3 className="font-display italic text-2xl text-ink mb-3">Get to VIP</h3>
            <ol className="list-decimal pl-5 space-y-2 text-sm text-ink-2 leading-relaxed">
              <li>Wager real money on Bra 3-Way. Every dollar bet counts toward your total wagered.</li>
              <li>Tier upgrades happen automatically the moment you cross the threshold.</li>
              <li>Benefits (cashback %, withdrawal speed, gifts) activate on the very next round after upgrade.</li>
              <li>Diamond tier unlocks a dedicated concierge — email <span className="text-ink font-mono">vip@bragame.local</span> if you cross $100k.</li>
            </ol>
            <div className="mt-5 flex flex-wrap gap-2">
              <Link to="/wallet/deposit"><Button variant="primary" size="lg">Deposit &amp; play</Button></Link>
              <Link to="/game"><Button variant="ghost" size="lg">Open the game <ArrowRight className="w-4 h-4" /></Button></Link>
            </div>
          </CardBody>
        </Card>
      </div>

      <ChatWidget />
    </AppShell>
  );
}

function TierCard({ tier, active, reached }) {
  const accent = TIER_ACCENT[tier.key] || TIER_ACCENT.bronze;
  return (
    <div
      className={
        "rounded-xl border p-5 relative overflow-hidden transition " +
        `bg-gradient-to-br ${accent.grad} ` +
        (active ? "shadow-glow-pink scale-[1.02]" : reached ? "opacity-90" : "opacity-60")
      }
      data-testid={`tier-${tier.key}`}
    >
      {active && (
        <div className="absolute top-2 right-2">
          <Badge variant="hot">You</Badge>
        </div>
      )}
      {reached && !active && (
        <div className="absolute top-2 right-2">
          <CheckCircle2 className="w-4 h-4 text-success" />
        </div>
      )}
      <Crown className={`w-6 h-6 mb-3 ${accent.chip}`} />
      <div className="font-display italic text-xl text-ink">{tier.label}</div>
      <div className="text-[10px] tracking-widest uppercase text-ink-3 mt-1">
        ${tier.threshold_usd.toLocaleString()}+ wagered
      </div>
      <div className="text-xs text-ink-2 mt-3 leading-relaxed min-h-[3rem]">{tier.benefits.description}</div>
      <div className="mt-3 pt-3 border-t border-line/50 text-[11px] text-ink font-mono flex items-center gap-2">
        <Award className="w-3 h-3 text-gold" />
        {tier.benefits.cashback_pct}% cashback · {tier.benefits.withdrawal_speed_hours}h withdrawal
      </div>
    </div>
  );
}

function BenefitStat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] tracking-widest uppercase text-ink-3">{label}</div>
      <div className="font-mono text-ink mt-1">{value}</div>
    </div>
  );
}
